import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from './components/layout/Sidebar';
import { TopBar } from './components/layout/TopBar';
import { ToastProvider } from './components/ui/Toast';
import { KeyboardShortcutsHelp } from './components/ui/KeyboardShortcutsHelp';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { PanelLinkProvider } from './contexts/PanelLinkContext';
import OverviewView from './views/OverviewView';
import MacroView from './views/MacroView';
import MacroMonitorView from './views/MacroMonitorView';
import MonetaryFinancialView from './views/MonetaryFinancialView';
import TradeExternalView from './views/TradeExternalView';
import FDIView from './views/FDIView';
import SectorsView from './views/SectorsView';
import PeersView from './views/PeersView';
import EventsView from './views/EventsView';
import NatexScoreView from './views/NatexScoreView';
import SettingsView from './views/SettingsView';

type View = 'overview' | 'macro' | 'macro-monitor' | 'monetary' | 'trade' | 'fdi' | 'sectors' | 'peers' | 'events' | 'natex-score' | 'settings';

function App() {
  const [activeView, setActiveView] = useState<View>('overview');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [openSearchTrigger, setOpenSearchTrigger] = useState(0);

  // Detect screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close mobile menu when view changes
  const handleViewChange = useCallback((view: View) => {
    setActiveView(view);
    setIsMobileMenuOpen(false);
  }, []);

  // Keyboard shortcuts
  const { showShortcutsHelp, setShowShortcutsHelp } = useKeyboardShortcuts({
    currentView: activeView,
    onViewChange: handleViewChange,
    onOpenSearch: () => setOpenSearchTrigger(prev => prev + 1),
  });

  const renderView = () => {
    switch (activeView) {
      case 'macro':
        return <MacroView />;
      case 'macro-monitor':
        return <MacroMonitorView />;
      case 'monetary':
        return <MonetaryFinancialView />;
      case 'trade':
        return <TradeExternalView />;
      case 'fdi':
        return <FDIView />;
      case 'sectors':
        return <SectorsView />;
      case 'peers':
        return <PeersView />;
      case 'events':
        return <EventsView />;
      case 'natex-score':
        return <NatexScoreView />;
      case 'settings':
        return <SettingsView />;
      case 'overview':
      default:
        return <OverviewView />;
    }
  };

  return (
    <PanelLinkProvider>
      <ToastProvider>
        <div className="h-screen flex bg-terminal-bg overflow-hidden">
          {/* Mobile Menu Backdrop */}
          {isMobileMenuOpen && (
            <div
              className="fixed inset-0 bg-black/60 z-40 md:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
          )}

          {/* Sidebar */}
          <Sidebar
            activeView={activeView}
            onViewChange={handleViewChange}
            isMobileOpen={isMobileMenuOpen}
            onMobileClose={() => setIsMobileMenuOpen(false)}
          />

          {/* Main Area */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Top Bar */}
            <TopBar
              onNavigate={(view) => handleViewChange(view as View)}
              onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              isMobile={isMobile}
              openSearchTrigger={openSearchTrigger}
            />

            {/* View Content */}
            {renderView()}
          </div>

          {/* Keyboard Shortcuts Help */}
          <KeyboardShortcutsHelp
            isOpen={showShortcutsHelp}
            onClose={() => setShowShortcutsHelp(false)}
          />
        </div>
      </ToastProvider>
    </PanelLinkProvider>
  );
}

export default App;
